import React from 'react';
import { Box, Button, Typography, Stack, Skeleton } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import type { NextAction } from '../../utils/dashboard';
import { formatMinutes } from '../../utils/dashboard';
import { dashboardCopy } from '../../copy/dashboard';

// ---------------------------------------------------------------------------
// NextActionCard — the hero of the new dashboard. Exactly one primary action,
// verb-first, time-bound when possible. Absence of data is handled with a
// single skeleton row, not a multi-section loading state.
// ---------------------------------------------------------------------------

interface NextActionCardProps {
  action: NextAction | null;
  loading?: boolean;
}

export function NextActionCard({ action, loading = false }: NextActionCardProps) {
  const navigate = useNavigate();

  if (loading) {
    return (
      <Box
        sx={{
          p: 4,
          borderRadius: 3,
          bgcolor: 'background.paper',
          border: 1,
          borderColor: 'divider',
        }}
      >
        <Skeleton variant="text" width={80} height={18} />
        <Skeleton variant="text" width="60%" height={40} sx={{ mt: 1 }} />
        <Skeleton variant="text" width={200} height={24} sx={{ mt: 1 }} />
        <Skeleton variant="rectangular" width={180} height={44} sx={{ mt: 3, borderRadius: 1.5 }} />
      </Box>
    );
  }

  if (!action) {
    return null;
  }

  const handlePrimary = () => {
    if (action.href) navigate(action.href);
  };

  const timePrefix = action.startTime
    ? `${action.startTime}${action.durationMinutes ? ` · ${formatMinutes(action.durationMinutes)}` : ''}`
    : action.durationMinutes
      ? formatMinutes(action.durationMinutes)
      : undefined;

  return (
    <Box
      sx={{
        p: { xs: 3, md: 4 },
        borderRadius: 3,
        bgcolor: 'background.paper',
        border: 1,
        borderColor: 'divider',
        // Subtle accent bar on the left anchors the card without making it
        // shout. Colour comes from the theme primary so dark/light both work.
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          left: 0,
          top: 0,
          bottom: 0,
          width: 4,
          bgcolor: 'primary.main',
        },
      }}
    >
      <Typography
        variant="overline"
        sx={{ color: 'text.secondary', letterSpacing: '0.12em', fontWeight: 600 }}
      >
        {dashboardCopy.nextActionHeader}
      </Typography>

      <Stack direction={{ xs: 'column', md: 'row' }} spacing={{ xs: 2, md: 4 }} alignItems={{ md: 'flex-end' }} sx={{ mt: 1.5 }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            variant="h4"
            sx={{
              fontWeight: 700,
              letterSpacing: '-0.02em',
              color: 'text.primary',
              // Balance the headline visually with the accent bar and the
              // timestamp on the right; keep it to one or two lines.
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              fontSize: { xs: '1.5rem', md: '1.875rem' },
            }}
          >
            {action.verb} {action.label}
          </Typography>
          {action.context && (
            <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
              {action.context}
            </Typography>
          )}
        </Box>

        {timePrefix && (
          <Typography
            variant="body2"
            sx={{
              color: 'text.secondary',
              fontVariantNumeric: 'tabular-nums',
              whiteSpace: 'nowrap',
            }}
          >
            {timePrefix}
          </Typography>
        )}
      </Stack>

      {action.href && (
        <Button
          variant="contained"
          size="large"
          onClick={handlePrimary}
          sx={{ mt: 3, py: 1.25, px: 3, fontWeight: 600 }}
        >
          {primaryLabelFor(action)}
        </Button>
      )}
    </Box>
  );
}

// Map the derived action verb to a button label. Keeping the label close to
// the heading reduces cognitive load — the button repeats the commitment the
// user already read, in a shorter form.
function primaryLabelFor(action: NextAction): string {
  switch (action.verb) {
    case 'Start':
      return dashboardCopy.startWorkItem;
    case 'Draft':
    case 'Review':
    case 'Decide on':
    case 'Follow up on':
    case 'Read':
    case 'Read briefing':
      return 'Open work item';
    case 'Prepare for':
      return dashboardCopy.startPreparingMeeting;
    case 'Plan tomorrow':
      return 'Plan tomorrow';
    default:
      return 'Open';
  }
}

export default NextActionCard;
